import React, {useEffect, useRef, useState} from 'react';
import {
    Camera,
    Wand2,
    Send,
    CalendarClock,
    AlertCircle, Pencil, Check
} from 'lucide-react';
import {
    aiGenerateAutomateImage,
    createCustomerPollAutomation,
    getAutomateMessageList,
    postAutomateMessage
} from "../../api/message/automate-messages.api";
import {getAllSpots} from "../../api/spot/spot.api";
import {useSearchParams} from "react-router-dom";
import {Dropdown, Modal, TextInput} from "omoplay";
import {listBenefit} from "../../api/benefit/benefit.api";
import {range} from "../../libs/helper";
import PasswordType from "../form/PasswordType";
import {kakaoImageUpload} from "../../api/file/file.api";
import SuspensePopup from "../SuspensePopup";
import TargetMessageForm from "../volatility-message/TargetMessageForm";
import {validateContentReason} from "../../api/message/volatility-message.api";
import Logo from "../../assets/images/omoplay_icon.svg"
import NoImage from "../../assets/images/no-image.svg"
import {useRecoilValue} from "recoil";
import {accountSelector} from "../../store/selector/selector";
import QuickAutomationSetup from "./QuickAutomationSetup";
import {generateBenefitImage} from "../../api/ai/ai.api";
import CustomerPollLinkSetting from "../form/automatemessage/CustomerPollLinkSetting";

/**
 * 가입 직후 'N분 뒤' 실시간으로 나가는 자동화. 쿠폰 없이 버튼 1개만 쓰고, 발송 조건은 '신규 가입 후 N분' 이다.
 * `CUSTOMER_POLL`(설문조사 요청, 2026-10-06) 은 종전에 `PLACE_REVIEW` 를 재활용하던 것을 분리한 조건이다.
 */
const SIGNUP_DELAY_CONDITIONS = ["PLACE_REVIEW", "KAKAO_MAP_REVIEW", "JOIN_TARGET", "CUSTOMER_POLL"]
const isSignupDelayCondition = (condition) => SIGNUP_DELAY_CONDITIONS.includes(condition)

const PASSWORD_TYPE_LABELS = { NONE: '전체 미설정', SINGLE: '전체 단일암호', BRANCH: '전체 진입점별 암호' }

// null 과 "NONE" 은 같은 의미('미설정')로 저장돼 있어 함께 취급한다.
const isNonePasswordType = (passwordType) => passwordType == null || passwordType === "NONE"

// --- 컴포넌트: 개별 메시지 설정 카드 ---
const AutomationCard = ({ data, benefits, selectedEntrypointId }) => {
    const account = useRecoilValue(accountSelector)
    const textareaRef = useRef(null);
    // 상태 관리
    const [messageData, setMessageData] = useState(data.messageData ?? { type: "FI" });
    const [isOn, setIsOn] = useState(data.status);

    // 설정 관련 상태
    const [sendDay, setSendDay] = useState(data.conditionDays);
    const [sendTime, setSendTime] = useState({ hour: `${data.sendHours}`, minute: `${data.sendMinutes}` });

    const isAdultOnly = data.adult === "Y"

    // 쿠폰 설정 상태 (팝업 대신 페이지 내 설정으로 변경)
    const hasCoupon = data.condition !== "EXPIRED_COUPON" && !isSignupDelayCondition(data.condition)
    const isCustomerPoll = data.condition === "CUSTOMER_POLL"
    // 설문 링크가 실제로 설문까지 닿는지(CustomerPollLinkSetting 이 판정해 알려준다). 켜기·저장 때 한 번 더 묻는 데 쓴다.
    const [pollLinkStatus, setPollLinkStatus] = useState(null)

    const [password, setPassword] = useState(data.password);
    const [passwordType, setPasswordType] = useState(data.passwordType);
    // 저장돼 있는(= DB 기준) 비밀번호 설정.
    // ① 안내 confirm 을 '실제로 미설정으로 바뀌는 순간'에만 띄우고(이미 미설정이면 정리할 것이 없다)
    // ② 취소했을 때 화면 선택을 DB 상태로 되돌리기 위해 따로 들고 있는다.
    const [savedPassword, setSavedPassword] = useState(data.password);
    const [savedPasswordType, setSavedPasswordType] = useState(data.passwordType);
    // '전체 미설정' 저장 확인 모달. null 이면 닫힘, `{nextOn}` 이면 확인 대기(확인 시 그 상태로 저장을 이어간다).
    const [passwordResetTarget, setPasswordResetTarget] = useState(null);

    // 비밀번호 UI 게이팅: '전체' 뷰는 3모드, 특정 진입점 뷰는 부모(전체) 모드에 종속
    const isGlobalView = selectedEntrypointId == null
    const parentPasswordType = data.parentPasswordType // 진입점 뷰에서만 백엔드가 내려줌
    const passwordLocked = !isGlobalView && (parentPasswordType == null || parentPasswordType === "NONE")
    const passwordAllowedModes = isGlobalView
        ? ['NONE', 'SINGLE', 'BRANCH']
        : (parentPasswordType === "BRANCH" ? ['BRANCH'] : ['NONE', 'BRANCH'])
    const passwordModeLabels = isGlobalView
        ? { NONE: '전체 미설정', SINGLE: '전체 단일암호', BRANCH: '전체 진입점별 암호' }
        : (parentPasswordType === "BRANCH"
            ? { BRANCH: '진입점별 암호 (전체 고정)' }
            : { NONE: '전체 단일암호', BRANCH: '진입점별 암호' })
    const parentPassword = data.parentPassword // 진입점 뷰에서 상속되는 전체 단일암호(비활성 표시용)
    // 부모가 '전체 진입점별 암호'면 진입점은 BRANCH 로 고정 표시(상속이어도 강조)
    const displayPasswordType = (!isGlobalView && parentPasswordType === "BRANCH")
        ? "BRANCH"
        : (passwordType ?? "NONE")

    const [isRepeat, setIsRepeat] = useState(data.isRepeat)

    const [testMessagePopup, setTestMessagePopup] = useState(false)

    const [showAiImage, setShowAiImage] = useState(false)

    const [requestMessage, setRequestMessage] = useState()

    // 핸들러
    const handleImageUpload = (e) => {
        kakaoImageUpload(e.target.files[0], "FI").then(({data}) => {
            if (data.code === "0") {
                setMessageData({...messageData, image: {...messageData.image, imgUrl: data.imageUrl}});
            } else {
                alert(data.message);
            }
        })
    };

    const handleAiGenerate = () => {
        if (hasCoupon && !messageData.coupon?.benefitId) {
            alert("쿠폰을 먼저 선택해주세요.")
            return
        }

        if (window.confirm("AI로 이미지를 생성합니다.\n진행하시겠습니까?")) {
            aiGenerateAutomateImage({
                condition: data.condition,
                benefitId: messageData.coupon?.benefitId,
                requestMessage: requestMessage
            }).then(({data}) => {
                if (!data.imageUrl) {
                    alert(data.message);
                } else {
                    setMessageData({...messageData, image: {...messageData.image, imgUrl: data.imageUrl}});
                    alert("생성이 완료되었습니다. 반드시 저장을 해주세요.")
                }
                setShowAiImage(false)
            }).catch((e) => {
                // AI 생성 실패(503) — 사유 alert. 종전엔 500 → /notfound 튕김.
                alert(e?.message || "AI 이미지 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.");
                setShowAiImage(false)
            })
        }
    };

    const handleTestSend = () => {
        setTestMessagePopup(true)
    };

    const handleSave = (nextOn) => {
        if (hasCoupon && !messageData.coupon?.benefitId) {
            alert("쿠폰을 먼저 선택해주세요.")
            return
        }
        if (hasCoupon && !messageData.coupon?.benefitExpiredDay) {
            alert("유효기간을 입력해주세요.")
            return
        }
        // 단일 암호를 선택했다면 반드시 4자리를 입력해야 저장(빈 암호 오류 상태 예방)
        if (passwordType === "SINGLE" && (!password || password.length < 4)) {
            alert("단일 암호는 4자리 숫자로 입력해주세요.")
            return
        }
        // '사용 중'으로 켜는 것은 실제 발송을 시작하는 행위다 → 내용 검증을 통과해야 켤 수 있다.
        // (끄는 것은 언제나 허용 — 발송을 멈추는 동작을 막으면 안 된다.)
        // 백엔드도 status=true 일 때만 같은 검증을 한다(AutomateMessageService.saveMessage).
        // 위 쿠폰·암호 검사 뒤에 두는 이유: 그쪽이 더 구체적인 안내라 먼저 보여주는 편이 낫다.
        if (nextOn) {
            const reason = validateContentReason(messageData)
            if (reason) {
                alert(`필수 항목을 모두 입력해야 자동화를 켤 수 있습니다.\n\n· ${reason}`)
                return
            }
            // 설문 링크가 설문까지 닿지 않는 상태로 켜면, 고객은 '종료된 이벤트' 나 설문 없는 이벤트를 받는다.
            // 막지는 않는다(설문 프로젝트를 곧 켤 예정일 수 있다) — 알고 켜게 한다.
            if (isCustomerPoll && pollLinkStatus && !pollLinkStatus.ok
                && !window.confirm(`설문 링크를 확인해 주세요.\n\n${pollLinkStatus.text}\n\n이대로 저장하시겠습니까?`)) {
                return
            }
        }
        // '전체'를 미설정으로 저장하면 백엔드가 이 자동화 메시지의 진입점 오버라이드까지
        // 미설정으로 정리한다(AutomateMessageService.saveMessage — automateId 로 한정된 update).
        // 되돌릴 수 없는 정리라 별도 모달로 범위를 보여주고 확인받은 뒤 저장을 이어서 진행한다.
        // 저장된 값이 이미 미설정이면 정리할 것이 없으므로 묻지 않는다
        // → ON/OFF 토글이나 내용만 고친 저장에서는 이 안내가 더 이상 뜨지 않는다.
        // (다른 검증을 모두 통과한 뒤에 두는 이유: 어차피 막힐 저장에 무거운 확인을 띄우지 않는다.)
        if (isGlobalView && isNonePasswordType(passwordType) && !isNonePasswordType(savedPasswordType)) {
            setPasswordResetTarget({ nextOn })
            return
        }

        saveMessage(nextOn)
    }

    // 실제 저장. 검증·확인을 통과한 뒤에만 호출된다.
    const saveMessage = (nextOn) => {
        // 부모(전체)가 미설정이면 이 진입점은 강제로 미설정으로 저장
        const effPasswordType = passwordLocked ? "NONE" : passwordType

        const message = {
            ...data,
            status: nextOn,
            entrypointId: selectedEntrypointId,
            messageData: messageData,
            conditionDays: sendDay,
            sendHours: sendTime.hour,
            sendMinutes: sendTime.minute,
            password: password,
            passwordType: effPasswordType,
            isRepeat: isRepeat,
            adult: isAdultOnly && "Y"
        }

        postAutomateMessage(message).then((res) => {
            // 실패(네트워크 오류 등)는 api 래퍼가 배열([])을 돌려준다.
            // 백엔드 검증 400 은 fetch.js 가 사유를 alert 하고 여기까지 오지 않는다(→ 상태도 안 바뀐다).
            if (Array.isArray(res)) {
                return alert("저장에 실패했습니다. 잠시 후 다시 시도해 주세요.")
            }
            setIsOn(nextOn)
            // 저장 성공 = 이제 이 값이 DB 기준값이다(같은 화면에서 다시 저장할 때 안내가 재차 뜨지 않도록).
            setSavedPasswordType(effPasswordType)
            setSavedPassword(password)
            alert("저장 되었습니다.")
        })
    }

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
        }
    }, [messageData.content]);

    return (
        <div className={`bg-white rounded-2xl shadow-sm border ${isOn ? 'border-blue-200 ring-1 ring-blue-100' : 'border-gray-200'} flex flex-col overflow-hidden transition-all duration-300`}>
            {/* 1. 헤더 & ON/OFF 토글 */}
            <div className="px-6 py-4 flex justify-between items-center bg-gray-50 border-b border-gray-100">
                <h3 className="font-bold text-gray-800">{data.name}</h3>
                <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${isOn ? 'text-blue-600' : 'text-gray-400'}`}>
            {isOn ? '사용 중' : '중지됨'}
          </span>
                    <button
                        onClick={() => {
                            // 예전에는 여기서 setIsOn 을 먼저 해버려, 저장이 400 으로 거절돼도
                            // 화면상 '사용 중'으로 켜져 보였다(실제 DB 는 꺼진 상태) → 저장 성공 후에만 반영한다.
                            handleSave(!isOn)
                        }}
                        className={`w-12 h-6 rounded-full transition-colors duration-200 relative ${isOn ? 'bg-blue-600' : 'bg-gray-300'}`}
                    >
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform duration-200 shadow-sm ${isOn ? 'left-7' : 'left-1'}`} />
                    </button>
                </div>
            </div>

            <div className="p-6 flex flex-col xl:flex-row gap-8 items-start justify-center bg-white">

                {/* 2. 폰 미리보기 (왼쪽) */}
                <div className="relative shrink-0 mx-auto xl:mx-0">
                    <div className="w-[300px] h-[600px] bg-white border-[8px] border-gray-800 rounded-[2.5rem] shadow-2xl overflow-hidden relative flex flex-col">
                        {/* 상단 바 */}
                        <div className="h-6 bg-[#abc1d1] w-full flex justify-between items-center px-4 pt-2">
                            <span className="text-[10px] font-bold text-black">10:30</span>
                            <div className="flex gap-1">
                                <div className="header-top-ingredient">
                                    <div>
                                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path fill-rule="evenodd" clip-rule="evenodd"
                                                  d="M11.2027 0.68308C11.2027 0.320927 10.9295 0.0273438 10.5925 0.0273438H9.98229C9.64527 0.0273438 9.37207 0.320927 9.37207 0.68308V6.36612C9.37207 6.72828 9.64527 7.02186 9.98229 7.02186H10.5925C10.9295 7.02186 11.2027 6.72828 11.2027 6.36612V0.68308ZM6.94981 1.42625H7.56003C7.89704 1.42625 8.17025 1.72688 8.17025 2.09772V6.35039C8.17025 6.72123 7.89704 7.02186 7.56003 7.02186H6.94981C6.61279 7.02186 6.33958 6.72123 6.33958 6.35039V2.09772C6.33958 1.72688 6.61279 1.42625 6.94981 1.42625ZM4.47168 2.94172H3.86145C3.52444 2.94172 3.25123 3.24618 3.25123 3.62174V6.34183C3.25123 6.7174 3.52444 7.02185 3.86145 7.02185H4.47168C4.80869 7.02185 5.0819 6.7174 5.0819 6.34183V3.62174C5.0819 3.24618 4.80869 2.94172 4.47168 2.94172ZM1.43919 4.34063H0.828971C0.491955 4.34063 0.21875 4.64074 0.21875 5.01094V6.35155C0.21875 6.72175 0.491955 7.02186 0.828971 7.02186H1.43919C1.77621 7.02186 2.04941 6.72175 2.04941 6.35155V5.01094C2.04941 4.64074 1.77621 4.34063 1.43919 4.34063Z"
                                                  fill="black"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <svg width="11" height="8" viewBox="0 0 11 8" fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path fill-rule="evenodd" clip-rule="evenodd"
                                                  d="M5.39568 1.49685C6.81851 1.49691 8.18694 2.02442 9.21812 2.97035C9.29577 3.04338 9.41989 3.04246 9.49637 2.96828L10.2386 2.24548C10.2774 2.20786 10.299 2.1569 10.2986 2.10387C10.2983 2.05085 10.2761 2.00014 10.2369 1.96296C7.5304 -0.539737 3.26053 -0.539737 0.554004 1.96296C0.514798 2.00011 0.492553 2.05081 0.492192 2.10383C0.49183 2.15685 0.513382 2.20783 0.552078 2.24548L1.29456 2.96828C1.37099 3.04257 1.4952 3.04349 1.57281 2.97035C2.60412 2.02436 3.97271 1.49685 5.39568 1.49685ZM5.39376 3.9112C6.17026 3.91115 6.91905 4.20391 7.49463 4.73259C7.57248 4.80762 7.69511 4.80599 7.771 4.72892L8.50743 3.97417C8.54622 3.93458 8.56773 3.88087 8.56718 3.82507C8.56662 3.76926 8.54403 3.71601 8.50446 3.67723C6.75169 2.02342 4.03731 2.02342 2.28455 3.67723C2.24496 3.71601 2.22237 3.76928 2.22185 3.82511C2.22133 3.88093 2.24292 3.93464 2.28179 3.97417L3.018 4.72892C3.09389 4.80599 3.21653 4.80762 3.29438 4.73259C3.86958 4.20426 4.61777 3.91153 5.39376 3.9112ZM6.83794 5.50934C6.83904 5.56961 6.81784 5.62772 6.77934 5.66994L5.5341 7.07425C5.4976 7.11552 5.44783 7.13874 5.39591 7.13874C5.34398 7.13874 5.29421 7.11552 5.25771 7.07425L4.01227 5.66994C3.9738 5.62768 3.95263 5.56956 3.95377 5.50929C3.95491 5.44902 3.97826 5.39195 4.0183 5.35155C4.81355 4.5999 5.97827 4.5999 6.77352 5.35155C6.81353 5.39198 6.83684 5.44907 6.83794 5.50934Z"
                                                  fill="black"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <svg width="17" height="9" viewBox="0 0 17 9" fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <rect opacity="0.35" x="0.676666" y="1.00723" width="13.73" height="6.86499"
                                                  rx="2.17391" stroke="black" stroke-width="0.572082"></rect>
                                            <path opacity="0.4"
                                                  d="M15.2656 3.45654V5.78805C15.726 5.59058 16.0254 5.13122 16.0254 4.6223C16.0254 4.11337 15.726 3.65401 15.2656 3.45654"
                                                  fill="black"></path>
                                            <rect x="1.53516" y="1.86523" width="12.0137" height="5.14874" rx="1.43021"
                                                  fill="black"></rect>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 채팅방 UI */}
                        <div className="flex-1 bg-[#abc1d1] p-3 overflow-y-auto hide-scrollbar">
                            <div className="flex items-start gap-2 mb-4">
                                <div className="w-8 h-8 rounded-full bg-white shrink-0">
                                    <img className="p-1" src={Logo} width="32" height="32" alt="logo"/>
                                </div>
                                <div>
                                    <div className="text-[10px]">(광고){account.channelName}</div>
                                    <div className="bg-white rounded-lg p-2 shadow-sm max-w-[220px]">

                                        {/* 이미지 영역 */}
                                        <div
                                            className="relative group rounded-md overflow-hidden bg-gray-100 aspect-square mb-2">
                                            <img src={messageData.image?.imgUrl ?? NoImage} alt="Preview"
                                                 className="w-full h-full"/>
                                            <div
                                                className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                                                <label
                                                    className="cursor-pointer bg-white/20 hover:bg-white/30 backdrop-blur text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1 transition">
                                                    <Camera size={12}/>
                                                    파일 선택
                                                    <input type="file" className="hidden" accept="image/png, image/jpeg"
                                                           onChange={handleImageUpload}/>
                                                </label>
                                                <button
                                                    onClick={() => setShowAiImage(true)}
                                                    className="bg-purple-500/80 hover:bg-purple-500 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1 transition"
                                                >
                                                    <Wand2 size={12}/>
                                                    AI 이미지 생성
                                                </button>
                                            </div>
                                        </div>

                                        {/* 텍스트 영역 */}
                                        <div className="space-y-1">
                                            <div className="relative group">
                                            <textarea
                                                value={messageData.content}
                                                ref={textareaRef}
                                                maxLength={400}
                                                onChange={(e) => setMessageData({
                                                    ...messageData,
                                                    content: e.target.value
                                                })}
                                                rows={5}
                                                className="w-full text-xs text-gray-600 leading-relaxed bg-transparent border border-transparent hover:border-gray-300 focus:border-blue-500 focus:bg-white rounded p-1 resize-none focus:outline-none transition-colors"
                                                placeholder="내용 입력"
                                            />
                                                <div className="absolute top-2 right-2 text-gray-300 group-hover:text-indigo-400 pointer-events-none opacity-100 transition-opacity">
                                                    <Pencil size={14} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 쿠폰 미리보기 (상태 반영) */}
                                        {hasCoupon && (
                                            <div className="kakao_link_coupon">
                                                <div className="kakao_coupon">
                                                    <div className="flex-align-center">
                                                        <div className="rounded-lg relative group w-max">
                                                            <input
                                                                type="text"
                                                                className="title w-[90px] text-[10px]"
                                                                maxLength={7}
                                                                value={messageData.coupon?.title?.replace(" 무료 쿠폰", "")}
                                                                onChange={(e) => setMessageData({...messageData, coupon: {...messageData.coupon, title: e.target.value + " 무료 쿠폰" } })}
                                                            />
                                                            <div className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-indigo-500 opacity-100 transition-opacity pointer-events-none">
                                                                <Pencil size={10} />
                                                            </div>
                                                        </div>
                                                        <div className="text-[10px]">무료 쿠폰</div>
                                                    </div>
                                                    <div className="desc">
                                                        <div className="rounded-lg relative group w-max">
                                                            <input
                                                                type="text"
                                                                className="title text-[10px]"
                                                                maxLength={12}
                                                                value={messageData.coupon?.description}
                                                                onChange={(e) => setMessageData({...messageData, coupon: {...messageData.coupon, description: e.target.value } })}
                                                            />
                                                            <div className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-indigo-500 opacity-100 transition-opacity pointer-events-none">
                                                                <Pencil size={10} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <span className="kakao-icon icon_coupon"></span>
                                            </div>
                                        )}
                                        {
                                            (isSignupDelayCondition(data.condition) || data.condition === "EXPIRED_COUPON") && (
                                                <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                                                    <div className="bg-gray-100 rounded-lg w-full relative group">
                                                        <input
                                                            type="text"
                                                            className="w-full bg-transparent text-gray-600 text-sm font-medium py-2 px-4 text-center focus:outline-none focus:ring-2 focus:ring-indigo-200 rounded-lg cursor-pointer hover:bg-gray-200/50 transition-colors"
                                                            maxLength={8}
                                                            placeholder="버튼명을 입력해 주세요 (최대 8자)"
                                                            value={(messageData.buttons?.[0]?.name) ?? ""}
                                                            onChange={(e) => {
                                                                setMessageData((prev => {
                                                                    if (!prev.buttons || prev.buttons.length === 0) {
                                                                        prev.buttons = [{}]
                                                                    }
                                                                    prev.buttons[0].name = e.target.value
                                                                    return (
                                                                        {...messageData, buttons: prev.buttons }
                                                                    )
                                                                }))
                                                            }}
                                                        />
                                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-indigo-500">
                                                            <Pencil size={12} />
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                    <p className="text-center text-xs text-gray-400 mt-3 flex items-center justify-center gap-1">
                        <AlertCircle size={12} /> 이미지/텍스트 클릭 수정 가능
                    </p>
                </div>

                {/* 3. 설정 패널 (오른쪽) */}
                <div className="flex-1 w-full space-y-6">

                    {/* 발송 조건 */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                            <span className="w-1 h-4 bg-black rounded-full"></span>
                            발송 조건 설정
                        </h4>
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 space-y-3">
                            <div className="flex items-center gap-2 text-sm text-gray-700 flex-wrap">
                                {
                                    data.condition === "BIRTHDAY" && (
                                        <>
                                            <span>생일</span>
                                            <input
                                                type="number"
                                                value={sendDay}
                                                onChange={(e) => setSendDay(e.target.value)}
                                                className="w-16 px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            />
                                            <span>일 전 자동 발송</span>
                                        </>
                                    )
                                }
                                {
                                    isSignupDelayCondition(data.condition) && (
                                        <>
                                            <span>신규 가입 후</span>
                                            <input
                                                type="number"
                                                value={sendTime.minute}
                                                onChange={(e) => setSendTime({...sendTime, minute: e.target.value})}
                                                className="w-16 px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            />
                                            <span>분 후 자동 발송</span>
                                        </>
                                    )
                                }
                                {
                                    data.condition === "EXPIRED_COUPON" && (
                                        <>
                                            <span>쿠폰 유효기간 만료</span>
                                            <input
                                                type="number"
                                                value={sendDay}
                                                onChange={(e) => setSendDay(e.target.value)}
                                                className="w-16 px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            />
                                            <span>일 전 자동 발송</span>
                                        </>
                                    )
                                }
                                {
                                    data.condition === "RAINY_DAY" && (
                                        <>
                                            <span>비/눈오는날</span>
                                            <span>자동 발송</span>
                                        </>
                                    )
                                }
                                {
                                    data.condition === "FIRST_ANNIVERSARY" && (
                                        <>
                                            <span>가입 후 1년 뒤</span>
                                            <span>자동 발송</span>
                                        </>
                                    )
                                }
                                {
                                    (data.condition === "RETARGETING" || data.condition === "REMIND") && (
                                        <>
                                            <span>신규 가입 후</span>
                                            <input
                                                type="number"
                                                value={sendDay}
                                                onChange={(e) => setSendDay(e.target.value)}
                                                className="w-16 px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            />
                                            <span>일 뒤 자동 발송</span>
                                            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${isRepeat ? 'bg-gray-800 text-white border-gray-800' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                                                <div className={`w-4 h-4 rounded border flex items-center justify-center ${isRepeat ? 'bg-white border-white' : 'bg-white border-gray-300'}`}>
                                                    {isRepeat && <Check size={10} className="text-black" />}
                                                </div>
                                                <input type="checkbox" className="hidden" checked={isRepeat}
                                                       onChange={(e) => setIsRepeat(e.target.checked)}
                                                />
                                                <span className="text-sm font-medium">한달마다 반복</span>
                                            </label>
                                        </>
                                    )
                                }
                            </div>
                            {
                                !isSignupDelayCondition(data.condition) && (
                                    <div className="flex items-center gap-2 text-sm text-gray-700">
                                        <span className="text-gray-500">발송 시간:</span>
                                        <select
                                            value={sendTime.hour}
                                            onChange={(e) => setSendTime({...sendTime, hour: e.target.value})}
                                            className="px-2 py-1 bg-white border border-gray-300 rounded focus:outline-none"
                                        >
                                            {range(13, 8).map(h => (
                                                <option key={h} value={h}>{h}시</option>
                                            ))}
                                        </select>
                                        <select
                                            value={sendTime.minute}
                                            onChange={(e) => setSendTime({...sendTime, minute: e.target.value})}
                                            className="px-2 py-1 bg-white border border-gray-300 rounded focus:outline-none"
                                        >
                                            <option value="00">00분</option>
                                            <option value="30">30분</option>
                                        </select>
                                    </div>
                                )
                            }
                        </div>
                    </div>

                    {isCustomerPoll && (
                        <CustomerPollLinkSetting
                            messageData={messageData}
                            onChange={setMessageData}
                            onStatusChange={setPollLinkStatus}
                        />
                    )}

                    {/* 쿠폰 설정 (인라인 수정 방식) */}
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                                <span className="w-1 h-4 bg-blue-600 rounded-full"></span>
                                쿠폰 설정
                            </h4>
                        </div>

                        {hasCoupon && (
                            <div className="p-4 bg-blue-50/50 rounded-lg border border-blue-100 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-600">첨부할 쿠폰</label>
                                        <select
                                            value={messageData.coupon?.benefitId}
                                            onChange={(e) => setMessageData({...messageData, coupon: {...messageData.coupon, benefitId: e.target.value}})}
                                            className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                        >
                                            <option disabled={true} selected={true}>선택하세요</option>
                                            {
                                                benefits.map(benefit => {
                                                    return (
                                                        <option value={benefit.id}>{benefit.name}</option>
                                                    )
                                                })
                                            }
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-600">유효 기간</label>
                                        <div className="flex gap-2">
                                            <div className="flex-align-center relative">발급 후</div>
                                            <div className="flex-1 flex items-center bg-white border border-gray-300 rounded-lg px-2">
                                                <input
                                                    type="number"
                                                    value={messageData.coupon?.benefitExpiredDay}
                                                    onChange={(e) => setMessageData({...messageData, coupon: {...messageData.coupon, benefitExpiredDay: e.target.value}})}
                                                    className="w-full py-1.5 text-sm bg-transparent outline-none text-right pr-1"
                                                />
                                                <span className="text-xs text-gray-500 whitespace-nowrap">일간</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2 text-xs text-blue-600 bg-white p-2 rounded border border-blue-100">
                                    <CalendarClock size={14} className="shrink-0 mt-0.5" />
                                    <span>
                    고객이 메시지를 받은 날로부터 <b>{messageData.coupon?.benefitExpiredDay}일 동안</b> 사용 가능한 쿠폰입니다.
                  </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 비밀번호 설정 */}

                    <div className="space-y-3">
                        <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                            <span className="w-1 h-4 bg-black rounded-full"></span>
                            비밀번호 설정
                        </h4>
                        {passwordLocked ? (
                            <p className="text-xs text-gray-500 leading-relaxed">
                                '전체' 설정이 '전체 미설정'이라 이 진입점에서는 비밀번호를 설정할 수 없습니다. 비밀번호를 쓰려면 '전체'에서 먼저 '전체 단일암호' 또는 '전체 진입점별 암호'로 설정하세요.
                            </p>
                        ) : (
                            <PasswordType
                                passwordType={displayPasswordType}
                                benefitPassword={password}
                                allowedModes={passwordAllowedModes}
                                modeLabels={passwordModeLabels}
                                showInheritedOnNone={!isGlobalView && parentPasswordType === "SINGLE"}
                                inheritedPassword={parentPassword}
                                helpText={<>
                                    전체 단일암호가 설정된 경우에도 '진입점별 암호'가 설정된 경우 '진입점별 암호'가 우선 적용됩니다.<br/>
                                    '진입점별 암호'가 설정된 쿠폰만 사용지점 추적 가능합니다.
                                </>}
                                onChangePassword={(password) => {
                                    setPassword(password);
                                }}
                                onChangePasswordType={(passwordType) => {
                                    setPasswordType(passwordType)
                                }}
                                onChangeValid={() => {}}
                            />
                        )}
                    </div>

                    {/* 테스트 발송 버튼 */}
                    <div className="pt-4 border-t border-gray-100 flex justify-end gap-4">
                        <button
                            onClick={handleTestSend}
                            className="flex items-center gap-2 px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-500 rounded-lg text-sm font-bold transition-colors shadow-lg"
                        >
                            <Send size={14} />
                            테스트 발송
                        </button>
                        <button
                            onClick={() => handleSave(isOn)}
                            className="flex items-center gap-2 px-6 py-2.5 bg-gray-900 hover:bg-black text-white rounded-lg text-sm font-bold transition-colors shadow-lg"
                        >
                            저장하기
                        </button>
                    </div>
                </div>
            </div>
            <SuspensePopup
                title={"테스트 발송하기"}
                visible={testMessagePopup}
                style={{width: "1000px"}}
                onClose={() => setTestMessagePopup(false)}
            >
                <TargetMessageForm isTest={true}
                                   onSelectedUserIds={() => {}}
                                   messageData={messageData}
                                   automateId={data.id}
                                   onSendMessage={() => setTestMessagePopup(false)}

                />
            </SuspensePopup>
            <SuspensePopup title="AI 이미지 생성" visible={showAiImage}
                           onClose={() => setShowAiImage(false)}
                           confirmText={<div className="flex">이미지 생성</div>}
                           onConfirm={() => {
                               handleAiGenerate()
                           }}
            >
                <div>
                    <div>AI 요청사항을 적으세요</div>
                    <TextInput />
                </div>
            </SuspensePopup>

            {/*
              비밀번호를 '전체 미설정'으로 되돌리는 저장은 진입점별 설정을 지우고 되돌릴 수 없다.
              alert/confirm 은 문구가 길어지면 스크롤이 생겨 읽히지 않으므로 전용 모달로 보여준다.
            */}
            <Modal
                visible={passwordResetTarget != null}
                title="비밀번호 설정 변경 확인"
                style={{maxWidth: "520px"}}
                confirmText="변경하고 저장"
                cancelText="취소"
                onConfirm={() => {
                    const nextOn = passwordResetTarget.nextOn
                    setPasswordResetTarget(null)
                    saveMessage(nextOn)
                }}
                onCancel={() => {
                    // 취소 = 아무것도 반영하지 않는다. 저장 요청을 보내지 않으므로 켜지지도 않고,
                    // 화면의 비밀번호 선택도 저장된 값으로 되돌려 DB 상태와 어긋나지 않게 한다.
                    setPasswordType(savedPasswordType)
                    setPassword(savedPassword)
                    setPasswordResetTarget(null)
                }}
            >
                <div className="space-y-3 text-sm text-gray-700">
                    <p className="leading-relaxed">
                        <b className="text-gray-900">'{data.name}'</b> 자동화 메시지의 비밀번호 설정을<br/>
                        <span className="text-gray-500">{PASSWORD_TYPE_LABELS[savedPasswordType] ?? '설정'}</span>
                        <span className="mx-1.5 text-gray-400">→</span>
                        <b className="text-red-600">전체 미설정</b> 으로 변경합니다.
                    </p>
                    <ul className="space-y-1.5 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-xs leading-relaxed">
                        <li className="flex gap-1.5">
                            <span className="text-gray-400">·</span>
                            <span>이 자동화 메시지의 <b>진입점별 설정이 모두 '미설정'으로 통일</b>됩니다.</span>
                        </li>
                        <li className="flex gap-1.5">
                            <span className="text-gray-400">·</span>
                            <span><b>사용지점 추적이 중단</b>됩니다. 사용지점은 '진입점별 암호'가 설정된 경우에만 기록됩니다(이미 발급된 혜택도 포함).</span>
                        </li>
                        <li className="flex gap-1.5">
                            <span className="text-gray-400">·</span>
                            <span>진입점 자체의 비밀번호, 다른 자동화 메시지·프로젝트 설정은 <b>바뀌지 않습니다.</b></span>
                        </li>
                    </ul>
                </div>
            </Modal>
        </div>
    );
};

// --- 메인 페이지 ---
const AutomationSettingsPage = () => {
    const account = useRecoilValue(accountSelector)
    /*
      내부 관리자(system) 계정 여부 — 판정 기준은 `account_permission.service_type='System'`
      한 곳이고 서버가 `GET /account` 에 담아 내려준다. 계정 id 하드코딩 금지.
    */
    const isSystemAdmin = account?.isSystemAdmin === true

    const [searchParams, setSearchParams] = useSearchParams();
    const [scenarios, setScenarios] = useState([]);
    const [selectedEntrypointId, setSelectedEntrypointId] = useState(searchParams.get("entrypointId"))
    const [entryPoints, setEntryPoints] = useState([{label: "전체", value: null}])
    const [benefits, setBenefits] = useState([]);

    useEffect(() => {
        getAllSpots().then(({data}) => {
            setEntryPoints([{label: "전체", value: null}, ...data.map(spot => ({label: spot.name, value: spot.id}))])
        })
        listBenefit(1, 100, "ACTIVE").then(({data}) => {
            setBenefits(data.benefits)
        })
    }, [])

    const loadScenarios = () => {
        getAutomateMessageList(selectedEntrypointId).then(({data}) => {
            setScenarios(data)
        })
    }

    useEffect(() => {
        loadScenarios()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEntrypointId]);

    /*
      설문조사 자동화 추가 — 관리자 전용(2026-10-12). 종전에는 만드는 화면이 없어 DB 에 직접 넣었다.
      '전체' 보기에서, 이 채널에 아직 없을 때만 보인다. 꺼진 상태로 만들어지므로 카드에서 설문 링크(진입점)를 고른 뒤 켠다.
      ⚠️ 실제 차단은 서버(`/sysadmin/**` = 관리자 전용)가 한다. 여기서는 버튼을 가릴 뿐이다.
    */
    const canAddCustomerPoll = isSystemAdmin && selectedEntrypointId == null
        && !scenarios.some(scenario => scenario.condition === "CUSTOMER_POLL")
    const handleAddCustomerPoll = () => {
        if (!window.confirm("이 채널에 '설문조사' 자동화를 추가합니다.\n꺼진 상태로 만들어지며, 카드에서 설문 링크(진입점)를 고른 뒤 켜면 발송됩니다.")) return
        createCustomerPollAutomation().then(result => {
            if (!result) return alert("설문조사 자동화를 추가하지 못했습니다. 잠시 후 다시 시도해 주세요.")
            alert(result.created ? "설문조사 자동화를 추가했습니다." : "이미 설문조사 자동화가 있습니다.")
            loadScenarios()
        })
    }

    return (
        <div className="min-h-screen">
            {/*
              'AI 빠른 자동화 일괄 세팅' — 2026-09-09 부터 내부 관리자 전용.

              한 번 누르면 선택한 조건 수만큼 이미지 생성이 돌아가고(`POST /automate-message/ai/generate`),
              고객이 자유롭게 눌러 **불필요한 AI 비용 + CS** 가 발생했다.
              ⇒ 기능의 용도를 '관리자 초기 세팅' 으로 좁혔다.

              ⚠️ 실제 차단은 서버 `SecurityConfig` 의 경로 규칙이 한다. 여기는 가림이다.
              ⬜ 같이 막지 않은 것 = 각 자동화 카드 안의 개별 AI 이미지 생성
                 (`handleAiGenerate` → `/automate-message/ai/generate/image`).
                 고객이 정상적으로 쓰는 기능이고 이번 요청 범위가 아니다.
            */}
            {isSystemAdmin && <QuickAutomationSetup />}
            <div className="max-w-7xl mx-auto space-y-8 bg-white p-8 font-sans rounded-2xl shadow-sm border transition-all">

                {/* 페이지 헤더 */}
                <div className="flex flex-col gap-2">
                    <h1 className="text-2xl font-bold text-gray-900">자동 메시지 관리</h1>
                    <p className="text-gray-600 text-sm">신규 고객 및 특정 조건에 맞춰 자동으로 발송되는 메시지를 설정합니다.</p>
                </div>

                <div className="flex-align-center gap-4">
                    <div className="text-[18px]">진입점 선택</div>
                    <Dropdown variants="default"
                              style={{width: "200px"}}
                              titleStyle={{backgroundColor: "white"}}
                              items={entryPoints}
                              initialValue={selectedEntrypointId}
                              onChanged={(items) => {
                                  setSelectedEntrypointId(items[0].value)
                                  setSearchParams({entrypointId: items[0].value}, {replace: true})
                              }} />
                    {canAddCustomerPoll && (
                        <button
                            type="button"
                            onClick={handleAddCustomerPoll}
                            className="ml-auto px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            + 설문조사 자동화 추가 <span className="text-xs text-gray-400">(관리자)</span>
                        </button>
                    )}
                </div>

                {/* 설정 카드 리스트 */}
                <div className="grid grid-cols-1 gap-10">
                    {scenarios.map(data => (
                        <AutomationCard key={JSON.stringify(data)} data={data} benefits={benefits} selectedEntrypointId={selectedEntrypointId} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AutomationSettingsPage;